/**
 * On-device attachment bytes (IndexedDB) so reopening a PDF/image does not
 * hit the Hub API every time. Scoped to the same mail cache window as threads.
 */

import { mailCacheWindowCutoff, readMailCacheWindowMs } from "./offlineCache";

const DB_NAME = "atw-hub-mail-attachments";
const DB_VERSION = 1;
const STORE = "blobs";

/** Skip caching very large files to protect quota. */
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_ENTRIES = 180;

export type AttachmentCacheKeyParts = {
  accountId?: string;
  threadId: string;
  messageId: string;
  attachmentId: string;
};

type AttachmentRecord = {
  key: string;
  blob: Blob;
  filename: string;
  mimeType: string;
  size: number;
  savedAt: number;
};

export function buildAttachmentCacheKey(parts: AttachmentCacheKeyParts): string {
  const acct = parts.accountId || "acct";
  return `${acct}::${parts.threadId}::${parts.messageId}::${parts.attachmentId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("idb open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  try {
    const db = await openDb();
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      const req = fn(store);
      tx.oncomplete = () => {
        if (req) resolve(req.result);
        else resolve(undefined);
      };
      tx.onerror = () => reject(tx.error || new Error("idb tx failed"));
    });
  } catch {
    return undefined;
  }
}

async function evictStaleAndOverflow(): Promise<void> {
  const cutoff = mailCacheWindowCutoff();
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.getAll();
      req.onsuccess = () => {
        const rows = (req.result || []) as AttachmentRecord[];
        const sorted = [...rows].sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
        const toDelete: string[] = [];
        for (const row of sorted) {
          if (cutoff != null && row.savedAt < cutoff) toDelete.push(row.key);
        }
        let keep = sorted.filter((row) => !toDelete.includes(row.key));
        if (keep.length > MAX_ENTRIES) {
          const overflow = keep.slice(MAX_ENTRIES);
          for (const row of overflow) toDelete.push(row.key);
        }
        for (const key of toDelete) store.delete(key);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error("idb evict failed"));
    });
  } catch {
    /* best-effort */
  }
}

export async function readCachedAttachmentObjectUrl(key: string): Promise<string | null> {
  if (!key) return null;
  const row = await withStore<AttachmentRecord>("readonly", (store) => store.get(key));
  if (!row?.blob?.size) return null;
  const windowMs = readMailCacheWindowMs();
  if (windowMs != null && row.savedAt && Date.now() - row.savedAt > windowMs) {
    void withStore("readwrite", (store) => store.delete(key));
    return null;
  }
  return URL.createObjectURL(row.blob);
}

export async function writeCachedAttachment(
  key: string,
  blob: Blob,
  meta: { filename?: string; mimeType?: string },
): Promise<void> {
  if (!key || !blob.size) return;
  if (blob.size > MAX_FILE_BYTES) return;
  const record: AttachmentRecord = {
    key,
    blob,
    filename: meta.filename || "attachment",
    mimeType: meta.mimeType || blob.type || "application/octet-stream",
    size: blob.size,
    savedAt: Date.now(),
  };
  await withStore("readwrite", (store) => store.put(record));
  void evictStaleAndOverflow();
}
